<template>
  <v-container
    fluid
    class="pa-7">
    <HeaderComponent
      class="pb-4"
      :title="$vuetify.lang.t('$vuetify.ecoSearch.title')" />

    <v-row class="pt-5">
      <v-col
        cols="lg-4"
        offsetLg="4">
        <form
          class="d-flex justify-stretch gap-1 mb-6"
          @submit.prevent="search">
          <v-text-field
            ref="searchInput"
            v-model="searchQuery"
            :label="$vuetify.lang.t('$vuetify.ecoSearch.input')"
            outlined
            singleLine
            hideDetails
            clearable />
          <v-btn
            height="auto"
            color="success"
            type="submit"
            class="d-md-none">
            <v-icon>$vuetify.icons.check</v-icon>
          </v-btn>
        </form>
      </v-col>
    </v-row>

    <v-progress-circular
      v-if="status === REQUEST_STATUS.LOADING"
      indeterminate
      color="primary" />
    <h4
      v-if="items[0] &&
        (status != REQUEST_STATUS.LOADING) &&
        (items.length != items.filter(item => item.attributes.status === 'GenesisCreditsAdded').length || !userLimitedRights) &&
        (items.length != items.filter(item => item.attributes.status === 'GenesisSendingToThirdPartiesApi').length || !userLimitedRights)"
      class="text-uppercase mb-3">
      {{ $vuetify.lang.t('$vuetify.ecoSearch.list') }}
    </h4>

    <p
      v-if="!items[0] && (status) && (status == REQUEST_STATUS.FAIL) && !searchQuery.includes('@')"
      class="text-red">
      {{ $vuetify.lang.t('$vuetify.ecoSearch.noItems') }}
    </p>
    <p
      v-if="items.length === 1 && items.some(item => item.attributes.status === 'GenesisCreditsAdded') && userLimitedRights"
      class="text-red">
      {{ $vuetify.lang.t('$vuetify.ecoSearch.alreadyCleared') }}
    </p>
    <p
      v-if="items.length > 1 && items.some(item => item.attributes.status === 'GenesisCreditsAdded') && items.some(item => item.attributes.status != 'GenesisCreditsAdded') === 0 && userLimitedRights"
      class="text-red">
      {{ $vuetify.lang.t('$vuetify.ecoSearch.alreadyClearedMore') }}
    </p>
    <p
      v-if="items.length === 1 && items.some(item => item.attributes.status === 'GenesisSendingToThirdPartiesApi') && userLimitedRights"
      class="text-red">
      {{ $vuetify.lang.t('$vuetify.ecoSearch.errorStatus') }}
    </p>
    <p
      v-if="!items[0] && (status) && (status == REQUEST_STATUS.FAIL) && searchQuery.includes('@')"
      class="text-red">
      {{ $vuetify.lang.t('$vuetify.ecoSearch.badEmail') }}
    </p>

    <v-card
      v-if="(status != REQUEST_STATUS.LOADING) &&
        (items.length != items.filter(item => item.attributes.status === 'GenesisCreditsAdded').length || !userLimitedRights) &&
        (items.length != items.filter(item => item.attributes.status === 'GenesisSendingToThirdPartiesApi').length || !userLimitedRights)"
      class="mb-5">
      <div
        v-for="item in items"
        :key="item.id">
        <v-container v-if="((item.attributes.status != 'GenesisCreditsAdded' || !userLimitedRights) && (item.attributes.status != 'GenesisSendingToThirdPartiesApi' || !userLimitedRights) && !userOnlyPreview) || (userOnlyPreview && item.attributes.status === 'GenesisCreditsAdded')">
          <v-row
            align="center"
            @click="$router.push({ name: 'processing.detail', params: { id: item.attributes.id } });">
            <div class="col-auto mr-auto">
              <v-card-title>
                {{ item.attributes.external_id }}
                <img
                  v-if="item.attributes.type === 3"
                  class="ml-2 packeta-icon"
                  src="/img/icons/packeta-50x50.webp"
                  alt="packeta"
                  loading="lazy">
                <img
                  v-if="item.attributes.type === 4"
                  class="ml-2 packeta-icon"
                  src="/img/icons/cp-50x50.webp"
                  alt="česká pošta"
                  loading="lazy">
              </v-card-title>
              <v-card-subtitle :class="lastSearchQuery.includes('@') ? 'pb-0' : ''">
                <span v-if="lastSearchQuery.includes('@')">Vytvořeno:</span>
                {{ formatDate(item.createdAt) }}
              </v-card-subtitle>
              <v-card-subtitle v-if="lastSearchQuery.includes('@')" class="pt-0">
                Změněno: {{ formatDate(item.updatedAt) }}
              </v-card-subtitle>
              <v-card-subtitle class="pt-0">
                {{ formatStatus(item.status) }}
              </v-card-subtitle>
            </div>
            <v-btn
              small
              color="transparent"
              class="col-auto action-button">
              <v-icon normal>
                $vuetify.icons.detail
              </v-icon>
            </v-btn>
          </v-row>
        </v-container>
        <hr
          v-if="((item.attributes.status != 'GenesisCreditsAdded' || !userLimitedRights) && (item.attributes.status != 'GenesisSendingToThirdPartiesApi' || !userLimitedRights) && !userOnlyPreview) || (userOnlyPreview && item.attributes.status === 'GenesisCreditsAdded')"
          role="separator"
          aria-orientation="horizontal"
          class="v-divider theme--light">
      </div>
    </v-card>

    <p
      v-if="customerNotFound"
      class="text-red">
      {{ $vuetify.lang.t('$vuetify.ecoSearch.customerNotFound') }}
    </p>

    <v-row class="mt-5">
      <v-btn
        v-if="searchQuery.includes('@')"
        class="mx-auto"
        color="primary"
        dark
        @click="createEco()">
        {{ $vuetify.lang.t('$vuetify.ecoSearch.newOrder') }}
      </v-btn>
    </v-row>
  </v-container>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

import HeaderComponent from '../../components/layout/HeaderComponent.vue';

import {formatDateTime} from '@/common/helpers/date';
import { formatStatusToReadable } from '@/common/helpers/status';

import REQUEST_STATUS from '../../common/enums/requestStatus';

export default {
  name: 'EcoSearch',

  components: {
    HeaderComponent,
  },

  data() {
    return {
      REQUEST_STATUS,
      searchQuery: "",
      lastSearchQuery: "",
      customerNotFound: false,
    }
  },

  computed: {
    ...mapGetters({
      items: 'orders/getAllItems',
      order: 'orders/getOrder',
      status: 'orders/getStatus',
      getUser: 'auth/getUser',
      userRoles: 'auth/getUserRoles',
      getCustomers: 'customers/getCustomers',
    }),

    userLimitedRights() {
      return this.userRoles.some(r => r.id === parseInt(process.env.VUE_APP_LIMITED_CREDIT_ADDITION_USER_ROLE_ID));
    },

    userOnlyPreview() {
      return this.userRoles.some(r => r.id === parseInt(process.env.VUE_APP_ONLY_PREVIEW_USER_ROLE_ID));
    },
  },

  created() {
    this.$nextTick(() => {
      this.$refs.searchInput.focus();
    });
  },

  destroyed() {
    this.items.length = 0;
  },

  methods: {
    ...mapActions({
      fetchOrders: 'orders/fetchAllItems',
      createOrder: 'orders/createEco',
      fetchCustomers: 'customers/fetchCustomers',
    }),

    formatDate(date) {
      return formatDateTime(date);
    },

    formatStatus(status) {
      return formatStatusToReadable(status);
    },

    createEco() {
      this.fetchCustomers(this.searchQuery).then(() => {
        this.customerNotFound = false;
        if(this.getCustomers.length) {
          this.createOrder({
            formData:{
              "external_id_type": 1,
              "customer_id": this.getCustomers[0].id,
              "first_name": this.getCustomers[0].firstName,
              "last_name": this.getCustomers[0].lastName,
              "email": this.searchQuery,
              "dial_code": (this.getCustomers[0].dialCode ? this.getCustomers[0].dialCode : '+420'),
              "phone": (this.getCustomers[0].phone ? this.getCustomers[0].phone : '601121312'),
              "type": 0,
              "note": "",
              "incorrectly_marked": true,
              "payload": {
                "stock_id": 175,
                "user_id": this.getUser.attributes.attributes.id,
              }
            },
          }).then(() => {
            this.$router.push({ name: 'processing.detail', params: { id: this.order.id } });
          });
        } else {
          this.customerNotFound = true;
        }
      })
    },

    async search() {
      this.lastSearchQuery = this.searchQuery;
      this.customerNotFound = false;

      await this.fetchOrders({searchQuery: this.searchQuery});

      if (this.items.length === 1 &&
        !this.searchQuery.includes("@") &&
        (!this.items.some(item => item.attributes.status === 'GenesisCreditsAdded') || !this.userLimitedRights ) &&
        (!this.items.some(item => item.attributes.status === 'GenesisSendingToThirdPartiesApi') || !this.userLimitedRights )
      ){
        this.$router.push({ name: 'processing.detail', params: { id: this.items[0].attributes.id } });
      }
    }
  }
}
</script>

<style scoped>
.packeta-icon {
  height: 2rem;
  border-radius: 30%;
}
</style>
